/**
 * Lead Assessment API Client
 *
 * Public API client for the Exit Readiness Assessment lead magnet.
 * These endpoints do not require authentication.
 */

import axios from 'axios';

// Create axios instance for public lead assessment endpoints
const leadApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // No credentials needed for public endpoints
});

// Response interceptor for error handling
leadApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Lead Assessment API Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Start a new assessment session
 * Creates a new anonymous assessment with a unique session token
 *
 * @returns {Promise<Object>} - { success, data: { session_token, assessment_id }, error }
 */
export const startLeadAssessment = async () => {
  try {
    const response = await leadApi.post('/public/lead-assessment/start');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error starting lead assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to start assessment'
    };
  }
};

/**
 * Fetch all assessment questions
 * Returns business, personal, valuation, and wealth gap questions
 *
 * @returns {Promise<Object>} - { success, data: { questions }, error }
 */
export const getLeadAssessmentQuestions = async () => {
  try {
    const response = await leadApi.get('/public/lead-assessment/questions');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching lead assessment questions:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch questions'
    };
  }
};

/**
 * Save an individual question response
 *
 * @param {Object} responseData - Response data
 * @param {string} responseData.session_token - Session token
 * @param {string} responseData.question_id - Question ID
 * @param {string} responseData.question_text - Question text
 * @param {string} responseData.category - Category (business, personal)
 * @param {string} responseData.question_type - Question type (quantitative, comparative, documentation, personal)
 * @param {number} [responseData.answer_value] - Answer value (1-6 for Likert scale)
 * @param {number} [responseData.answer_numeric] - Numeric answer (for quantitative questions)
 * @returns {Promise<Object>} - { success, data: { score, answer_value }, error }
 */
export const saveLeadAssessmentResponse = async (responseData) => {
  try {
    const response = await leadApi.put('/public/lead-assessment/response', responseData);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error saving lead assessment response:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to save response'
    };
  }
};

/**
 * Calculate assessment scores and generate results
 *
 * @param {string} sessionToken - Session token
 * @param {Object} valuationData - Valuation context data
 * @param {number} valuationData.annual_revenue - Annual revenue
 * @param {number} valuationData.ebitda_amount - EBITDA amount
 * @param {number} valuationData.valuation_multiple - Valuation multiple
 * @param {Object} wealthData - Wealth gap data
 * @param {number} wealthData.desired_exit_proceeds - Target exit proceeds
 * @param {number} [wealthData.current_net_worth] - Current net worth
 * @returns {Promise<Object>} - { success, data: { scores, recommendations, valuation, wealth_gap }, error }
 */
export const calculateLeadAssessmentResults = async (sessionToken, valuationData = {}, wealthData = {}) => {
  try {
    const response = await leadApi.post('/public/lead-assessment/calculate', {
      session_token: sessionToken,
      valuation_data: valuationData,
      wealth_data: wealthData
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error calculating lead assessment results:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to calculate results'
    };
  }
};

/**
 * Submit lead contact information after assessment completion
 *
 * @param {string} sessionToken - Session token
 * @param {Object} leadData - Lead contact information
 * @param {string} leadData.full_name - Full name
 * @param {string} leadData.business_name - Business name
 * @param {string} leadData.email - Email address
 * @param {string} [leadData.phone] - Phone number
 * @returns {Promise<Object>} - { success, data: { lead_id, message }, error }
 */
export const submitLeadInformation = async (sessionToken, leadData) => {
  try {
    const response = await leadApi.post('/public/lead-assessment/submit-lead', {
      session_token: sessionToken,
      ...leadData
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error submitting lead information:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to submit lead information'
    };
  }
};

/**
 * Retrieve complete assessment results by session token
 *
 * @param {string} sessionToken - Session token
 * @returns {Promise<Object>} - { success, data: { assessment, readiness_level, recommendations, lead_info }, error }
 */
export const getLeadAssessmentResults = async (sessionToken) => {
  try {
    const response = await leadApi.get(`/public/lead-assessment/results/${sessionToken}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching lead assessment results:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch results'
    };
  }
};

/**
 * Generate and download PDF report
 *
 * @param {string} sessionToken - Session token
 * @returns {Promise<Object>} - { success, data: Blob, error }
 */
export const downloadLeadAssessmentPDF = async (sessionToken) => {
  try {
    const response = await leadApi.get(`/public/lead-assessment/pdf/${sessionToken}`, {
      responseType: 'blob' // Important for binary PDF data
    });

    // Extract filename from Content-Disposition header
    let filename = `Exit_Readiness_Assessment_${sessionToken.substring(0, 8)}.pdf`; // Default fallback
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create a blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      data: blob
    };
  } catch (error) {
    console.error('Error downloading lead assessment PDF:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to download PDF'
    };
  }
};

/**
 * Session Storage Helpers
 * Store session token in sessionStorage (not localStorage to maintain privacy)
 */

export const saveSessionToken = (token) => {
  try {
    sessionStorage.setItem('lead_assessment_session_token', token);
    return true;
  } catch (error) {
    console.error('Error saving session token:', error);
    return false;
  }
};

export const getSessionToken = () => {
  try {
    return sessionStorage.getItem('lead_assessment_session_token');
  } catch (error) {
    console.error('Error getting session token:', error);
    return null;
  }
};

export const clearSessionToken = () => {
  try {
    sessionStorage.removeItem('lead_assessment_session_token');
    return true;
  } catch (error) {
    console.error('Error clearing session token:', error);
    return false;
  }
};

/**
 * Local Storage for Responses (Auto-save Progress)
 * Store responses locally to prevent data loss on refresh
 */

export const saveResponsesLocally = (responses) => {
  try {
    localStorage.setItem('lead_assessment_responses', JSON.stringify(responses));
    return true;
  } catch (error) {
    console.error('Error saving responses locally:', error);
    return false;
  }
};

export const getLocalResponses = () => {
  try {
    const responses = localStorage.getItem('lead_assessment_responses');
    return responses ? JSON.parse(responses) : {};
  } catch (error) {
    console.error('Error getting local responses:', error);
    return {};
  }
};

export const clearLocalResponses = () => {
  try {
    localStorage.removeItem('lead_assessment_responses');
    return true;
  } catch (error) {
    console.error('Error clearing local responses:', error);
    return false;
  }
};

/**
 * Local Storage for Valuation Data
 * Store valuation inputs locally to persist data when navigating back
 */

export const saveValuationLocally = (valuationData) => {
  try {
    localStorage.setItem('lead_assessment_valuation', JSON.stringify(valuationData));
    return true;
  } catch (error) {
    console.error('Error saving valuation data locally:', error);
    return false;
  }
};

export const getLocalValuation = () => {
  try {
    const valuation = localStorage.getItem('lead_assessment_valuation');
    return valuation ? JSON.parse(valuation) : {};
  } catch (error) {
    console.error('Error getting local valuation:', error);
    return {};
  }
};

export const clearLocalValuation = () => {
  try {
    localStorage.removeItem('lead_assessment_valuation');
    return true;
  } catch (error) {
    console.error('Error clearing local valuation:', error);
    return false;
  }
};

/**
 * Local Storage for Wealth Gap Data
 * Store wealth gap inputs locally to persist data when navigating back
 */

export const saveWealthDataLocally = (wealthData) => {
  try {
    localStorage.setItem('lead_assessment_wealth', JSON.stringify(wealthData));
    return true;
  } catch (error) {
    console.error('Error saving wealth data locally:', error);
    return false;
  }
};

export const getLocalWealthData = () => {
  try {
    const wealth = localStorage.getItem('lead_assessment_wealth');
    return wealth ? JSON.parse(wealth) : {};
  } catch (error) {
    console.error('Error getting local wealth data:', error);
    return {};
  }
};

export const clearLocalWealthData = () => {
  try {
    localStorage.removeItem('lead_assessment_wealth');
    return true;
  } catch (error) {
    console.error('Error clearing local wealth data:', error);
    return false;
  }
};

export default leadApi;
